import type {EmploymentsResponse, SelfEmploymentProfile} from '@rocket-ui/lk-api';
import type {WorkStatusConnectionState, WorkStatusConnectionStates} from './workStatusConnection';
import {
	BIND_REQUEST_EXTERNAL_STATUS,
	getSelfEmploymentProvider,
	getSelfEmploymentSnapshot,
	isNotSelfEmployedEmploymentStatus,
} from './selfEmploymentProfiles';

type SelfEmploymentSnapshot = EmploymentsResponse['selfEmployment'] | SelfEmploymentProfile | undefined;

const mapActiveEmploymentState = (
	employment: {active?: boolean; current?: boolean} | undefined,
): WorkStatusConnectionState | undefined => {
	if (employment?.active !== true) {
		return undefined;
	}

	return employment.current === true ? 'current' : 'connected';
};

const isChosenEmployment = (employment: {active?: boolean; current?: boolean} | undefined): boolean =>
	employment?.active === true || employment?.current === true;

/**
 * Для баннера «Не хватает данных»: занятость уже выбрана, если `active` (или `current`)
 * есть у физлица, ИП, представителя или любого профиля в `selfEmploymentProfiles`.
 * Состояние плитки (pending, ошибка, неподписанное соглашение) здесь не важно.
 * Поле `selfEmployment` не учитываем.
 */
export const hasActiveEmploymentStatus = (data: EmploymentsResponse | null | undefined): boolean => {
	if (!data) {
		return false;
	}

	if (
		isChosenEmployment(data.individual) ||
		isChosenEmployment(data.individualEntrepreneur) ||
		isChosenEmployment(data.representativeOfTheCompany)
	) {
		return true;
	}

	return Boolean(data.selfEmploymentProfiles?.some((profile) => isChosenEmployment(profile)));
};

const getAgreementStatus = (selfEmployment: SelfEmploymentSnapshot): string | null | undefined => {
	if (!selfEmployment || !('agreement' in selfEmployment)) {
		return undefined;
	}

	return selfEmployment.agreement?.status;
};

const getExternalStatus = (selfEmployment: SelfEmploymentSnapshot): string | null | undefined => {
	if (!selfEmployment || !('externalStatus' in selfEmployment)) {
		return undefined;
	}

	return selfEmployment.externalStatus;
};

const mapSelfEmploymentIssue = (
	selfEmployment: SelfEmploymentSnapshot,
): WorkStatusConnectionState | undefined => {
	if (!selfEmployment) {
		return undefined;
	}

	const {status, error} = selfEmployment;
	const agreementStatus = getAgreementStatus(selfEmployment);

	// agreement.sent — соглашение не подписано. Плитка остаётся кликабельной (фрейм),
	// ошибка «Подпишите соглашение» рисуется отдельно, если фрейм уже открывали.
	if (agreementStatus === 'sent') {
		return undefined;
	}

	// Конкретные ошибки важнее pending (в ответе может быть status:pending + error:need_permissions).
	if (status === 'need_permissions' || error === 'need_permissions') {
		return 'error_need_permissions';
	}

	if (isNotSelfEmployedEmploymentStatus(status) || isNotSelfEmployedEmploymentStatus(error)) {
		return 'error_not_selfemployed';
	}

	if (error === 'withdrawn') {
		return 'error_withdrawn';
	}

	if (status === 'just_sent_inn_for_check') {
		return 'pending';
	}

	if (error === 'pending' || status === 'pending') {
		if (
			agreementStatus === 'signed' &&
			getExternalStatus(selfEmployment) === BIND_REQUEST_EXTERNAL_STATUS
		) {
			return 'error_need_permissions';
		}

		if (agreementStatus === 'signed' && getSelfEmploymentProvider() === 'alfa') {
			return 'pending';
		}

		return 'pending';
	}

	// Текст ошибки бэка (validation.failed и т.п.), не код из enum.
	if (typeof error === 'string' && error.trim()) {
		return 'error';
	}

	return undefined;
};

/**
 * Состояние плитки СМЗ по одному профилю из `selfEmploymentProfiles`.
 * Приоритет: current (галочка) → ошибка/pending (красный/лососевый) → active (жёлтый).
 */
export const mapSelfEmploymentProfileConnectionState = (
	selfEmployment: SelfEmploymentSnapshot,
): WorkStatusConnectionState | undefined => {
	if (!selfEmployment) {
		return undefined;
	}

	// Пока соглашение не подписано, плитка не считается подключённой: клик открывает фрейм.
	if (getAgreementStatus(selfEmployment) === 'sent') {
		return undefined;
	}

	if (selfEmployment.current === true) {
		return 'current';
	}

	const issue = mapSelfEmploymentIssue(selfEmployment);

	if (issue) {
		return issue;
	}

	return selfEmployment.active === true ? 'connected' : undefined;
};

/** Маппинг GET /employment → состояния плиток на экране выбора статуса */
export const mapEmploymentsToWorkStatusConnections = (
	data: EmploymentsResponse,
): WorkStatusConnectionStates => {
	const result: WorkStatusConnectionStates = {};

	const selfEmploymentState = mapSelfEmploymentProfileConnectionState(getSelfEmploymentSnapshot(data));

	if (selfEmploymentState) {
		result.self_employment = selfEmploymentState;
	}

	const individualState = mapActiveEmploymentState(data.individual);

	if (individualState) {
		result.individual = individualState;
	}

	const individualEntrepreneurState = mapActiveEmploymentState(data.individualEntrepreneur);

	if (individualEntrepreneurState) {
		result.individual_entrepreneur = individualEntrepreneurState;
	}

	return result;
};

/** API — источник правды; local только для статусов, которых ещё нет в ответе. */
export const mergeWorkStatusConnections = (
	fromApi: WorkStatusConnectionStates,
	local: WorkStatusConnectionStates,
): WorkStatusConnectionStates => ({
	self_employment: fromApi.self_employment ?? local.self_employment ?? 'available',
	individual: fromApi.individual ?? local.individual ?? 'available',
	individual_entrepreneur:
		fromApi.individual_entrepreneur ?? local.individual_entrepreneur ?? 'available',
});
